import { computed, Directive, inject, input, resource } from '@angular/core';

import { SchemaNode } from '../model/schema-node.types';
import { SchemaReference } from '../model/schema-reference.types';
import { NO_DOCUMENT_LOADER, resolveSchemaReference } from '../reference/compose-schema';
import { reportUnresolved } from '../reference/report-unresolved';
import { SCHEMA_DOCUMENT_LOADER } from '../reference/schema-document-loader.token';
import { SCHEMA_REFERENCE_CHAIN } from '../reference/schema-reference-chain.token';

/** What a reference's place shows: the part once it arrived, or meanwhile one of the reference's `$refStates`. */
export interface SchemaReferencePhase {
  phase: 'ready' | 'loading' | 'error';
  node: SchemaNode | undefined;
}

const NO_CHAIN = (): readonly string[] => [];

/**
 * The part of a reference, for as long as the reference shows (670359). Internal to `kros-schema-node-outlet`, which
 * creates it only for a node that stands for another document, so a plain node carries none of it: the outlet renders
 * every node of every schema page.
 *
 * It loads the part through `SCHEMA_DOCUMENT_LOADER`, with the part's eager references composed into it, and the load
 * aborts when the directive goes — when the reference stops showing. It hands the keys of the documents resolved above,
 * its own last, to the outlets below through `SCHEMA_REFERENCE_CHAIN`, so a reference back to one of them fails as a
 * cycle.
 */
@Directive({
  selector: '[krosSchemaReferencePart]',
  exportAs: 'krosSchemaReferencePart',
  providers: [
    { provide: SCHEMA_REFERENCE_CHAIN, useFactory: (): (() => readonly string[]) => inject(SchemaReferencePartDirective).chainBelow },
  ],
})
export class SchemaReferencePartDirective {
  readonly reference = input.required<SchemaReference>({ alias: 'krosSchemaReferencePart' });

  readonly #documentLoader = inject(SCHEMA_DOCUMENT_LOADER, { optional: true }) ?? NO_DOCUMENT_LOADER;

  readonly #chainAbove = inject(SCHEMA_REFERENCE_CHAIN, { optional: true, skipSelf: true }) ?? NO_CHAIN;

  /** A failure warns in dev mode unless the load was aborted first: a hidden or destroyed place shows nothing to explain. */
  readonly #part = resource({
    params: () => this.reference(),
    loader: ({ params: reference, abortSignal }) =>
      resolveSchemaReference(reference, this.#documentLoader, this.#chainAbove(), abortSignal).catch((failure: unknown) => {
        if (!abortSignal.aborted) {
          reportUnresolved(reference, failure);
        }

        throw failure;
      }),
  });

  /** The keys of the documents resolved down to this part, its own last, for the outlets below it. */
  readonly chainBelow = (): readonly string[] => [...this.#chainAbove(), this.reference().$ref];

  readonly shown = computed<SchemaReferencePhase>(() => {
    if (this.#part.hasValue()) {
      return { phase: 'ready', node: this.#part.value() };
    }

    const states = this.reference().$refStates;

    return this.#part.status() === 'error' ? { phase: 'error', node: states?.error } : { phase: 'loading', node: states?.loading };
  });
}
